import React, { useEffect, useState } from "react";
import { Loader2, ShoppingBag, Sparkles, Lock, Check } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { useToast } from "../hooks/use-toast";
import { usePageDescription } from "../lib/usePageDescription";
import PageHero from "../components/PageHero";

const GlowSwatch = ({ item, size = 80 }) => {
  const style = item.gradient
    ? { background: item.gradient, boxShadow: `0 0 ${size / 2}px ${item.color}` }
    : { backgroundColor: item.color, boxShadow: `0 0 ${size / 2}px ${item.color}` };
  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      <div className="absolute inset-0 rounded-full opacity-50 blur-md" style={style} />
      <div className="relative rounded-full" style={{ ...style, width: size * 0.7, height: size * 0.7 }} />
      <div className="pixel-text absolute text-xl" title={item.icon}>{item.icon}</div>
    </div>
  );
};

const BackgroundSwatch = ({ item, size = 80 }) => (
  <div
    className="relative overflow-hidden rounded-lg ring-1 ring-white/10"
    style={{ width: size, height: size }}
  >
    <img
      src={item.image_url}
      alt={item.name}
      className="h-full w-full object-cover"
      loading="lazy"
      onError={(e) => { e.currentTarget.style.opacity = "0.3"; }}
    />
    <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-black/40 to-transparent" />
  </div>
);

const ItemSwatch = ({ item, size }) =>
  (item.type === "background")
    ? <BackgroundSwatch item={item} size={size} />
    : <GlowSwatch item={item} size={size} />;

const CATEGORY_LABEL = {
  Saison: "Saisonal",
  Element: "Element",
  Stil: "Stil",
  Neon: "Neon",
  Limited: "Limited Edition",
  Hintergrund: "Profil-Hintergründe",
};

const Shop = () => {
  const pd = usePageDescription("shop", {
    title: "Glow-Shop",
    intro: "Sammle Punkte und schalte exklusive Glow-Farben für deine Kommentare frei. Saisonale Glows sind nur zeitweise verfügbar.",
    keywords: ["Glows", "Hintergründe", "Punkte", "Belohnungen"],
  });
  const [items, setItems] = useState([]);
  const [pointsInfo, setPointsInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(null);
  const { user, userToken } = useAuth();
  const { toast } = useToast();

  const load = async () => {
    const tasks = [api.listShop()];
    if (userToken) tasks.push(api.myPoints(userToken));
    const [shop, mp] = await Promise.all(tasks);
    setItems(shop || []);
    setPointsInfo(mp || null);
  };

  useEffect(() => {
    (async () => {
      try { await load(); } finally { setLoading(false); }
    })();
  }, [userToken]);

  const onRedeem = async (item) => {
    if (!userToken) {
      toast({ title: "Login erforderlich", description: "Melde dich an, um Items einzulösen.", variant: "destructive" });
      return;
    }
    setRedeeming(item.id);
    try {
      const r = await api.redeemShop(userToken, item.id);
      toast({ title: `${item.name} freigeschaltet!`, description: `Neuer Stand: ${r.points} Punkte. Glow aktiviert.` });
      await load();
    } catch (err) {
      toast({ title: "Nicht eingelöst", description: err?.response?.data?.detail || "Unbekannter Fehler", variant: "destructive" });
    } finally { setRedeeming(null); }
  };

  const onActivate = async (slug) => {
    if (!userToken) return;
    try {
      const r = await api.setActiveGlow(userToken, slug);
      toast({ title: r.active_glow_slug ? "Glow aktiviert" : "Glow deaktiviert" });
      await load();
    } catch (err) {
      toast({ title: "Fehler", description: err?.response?.data?.detail || "", variant: "destructive" });
    }
  };

  const myPoints = pointsInfo?.points || 0;
  const unlocked = new Set(pointsInfo?.history ? [] : []); // Unlock-Set wird ueber /me API ergaenzt
  const activeSlug = user?.active_glow_slug;

  // Gruppieren nach Kategorie
  const byCategory = items.reduce((acc, it) => {
    const k = it.category || "Stil";
    (acc[k] = acc[k] || []).push(it);
    return acc;
  }, {});

  return (
    <section className="mx-auto max-w-7xl px-4 py-10" data-testid="shop-page">
      <PageHero
        eyebrow={pd.eyebrow || "Punkte-Shop"}
        icon={ShoppingBag}
        title={pd.title}
        keywords={pd.keywords}
        intro={pd.intro}
        testId="shop-hero"
        aside={
          userToken && pointsInfo ? (
            <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 px-5 py-3 text-right">
              <div className="text-[10px] uppercase tracking-wider text-amber-200">Dein Punktestand</div>
              <div className="pixel-text text-3xl text-amber-300" data-testid="shop-my-points">{myPoints.toLocaleString("de-DE")}</div>
              <div className="text-[10px] text-zinc-400">{pointsInfo.tier?.icon} {pointsInfo.tier?.label}</div>
            </div>
          ) : null
        }
      />

      {loading ? (
        <div className="flex items-center justify-center py-20 text-zinc-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="mt-8 rounded-xl border border-white/5 bg-[#10141b] p-12 text-center text-sm text-zinc-500">
          Aktuell sind keine Items im Shop verfügbar.
          <div className="mt-2 text-[11px] text-zinc-600">Schau bald wieder vorbei – wir geben regelmäßig neue Glows frei.</div>
        </div>
      ) : (
        <div className="mt-8 space-y-8">
          {Object.entries(byCategory).map(([cat, list]) => (
            <div key={cat}>
              <div className="mb-3 text-xs font-bold uppercase tracking-[0.22em] text-[#9d7bff]">
                {CATEGORY_LABEL[cat] || cat} ({list.length})
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {list.map((it) => {
                  const isLimited = cat === "Limited";
                  const isActive = activeSlug === it.slug;
                  const canAfford = myPoints >= it.cost;
                  return (
                    <div
                      key={it.id}
                      data-testid={`shop-item-${it.slug}`}
                      className={`rounded-2xl border bg-[#0f141b] p-4 transition-colors ${isActive ? "border-amber-400/60 bg-amber-500/[0.04]" : isLimited ? "border-amber-500/30" : "border-white/10"}`}
                    >
                      <div className="flex gap-4">
                        <ItemSwatch item={it} size={80} />
                        <div className="min-w-0 flex-1">
                          <div className="text-base font-bold text-white">{it.name}</div>
                          <div className="mt-0.5 text-[10px] uppercase tracking-wider text-zinc-500">
                            {it.type === "background" ? "Profil-Hintergrund" : (it.season || cat)}
                          </div>
                          {it.description && <div className="mt-2 text-xs text-zinc-400 line-clamp-2">{it.description}</div>}
                        </div>
                      </div>
                      <div className="mt-3 flex items-center justify-between gap-2">
                        <div className="pixel-text text-lg text-amber-300">{it.cost} <span className="text-[10px] uppercase tracking-wider text-zinc-500">Punkte</span></div>
                        {isActive ? (
                          <Button
                            type="button"
                            disabled
                            className="rounded-lg border border-amber-400/40 bg-amber-400/20 px-3 py-1.5 text-xs font-semibold text-amber-200"
                          >
                            <Check className="mr-1 h-3.5 w-3.5 inline" /> Aktiv
                          </Button>
                        ) : (
                          <Button
                            type="button"
                            data-testid={`shop-redeem-${it.slug}`}
                            onClick={() => onRedeem(it)}
                            disabled={!userToken || redeeming === it.id || !canAfford}
                            className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${canAfford ? "btn-primary" : "bg-white/5 text-zinc-500"}`}
                          >
                            {redeeming === it.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              : !userToken ? <><Lock className="mr-1 h-3.5 w-3.5 inline" /> Login</>
                              : !canAfford ? `${it.cost - myPoints} Punkte fehlen`
                              : <><Sparkles className="mr-1 h-3.5 w-3.5 inline" /> Einlösen</>}
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default Shop;
